// Library methods
import { useTranslation } from "react-i18next";
import { useCallback, useMemo } from "react";
import i18next from "i18next";

// Constants
import {
  DEFAULT_DECIBEL_VALUE_ESTERMAN,
  DEFAULT_DECIBEL_VALUE_FULL_120,
  DEFAULT_DECIBEL_VALUE_SUPERIOR_36,
  DEFAULT_DECIBEL_VALUE_SCREENING,
  DEFAULT_STARTING_ROW_INDEX,
  DefaultExamCardType,
  examCardEnums,
  ExamCardSectionNameType,
  eyeTypeEnums,
  fixationLossMethodEnums,
  fixationPointShapeEnums,
  positionTypeEnums,
  speedTypeEnums,
  stimulusColorEnums,
  stimulusTypeEnums,
  VISUAL_FIELD_PATH,
  gridTypeEnums,
  DEFAULT_SIM_ENVIRONMENT,
  stereoTargetStyleOptions,
  aniseikoniaTargetStyleOptions,
  DEFAULT_ANISEIKONIA_SETTINGS_PER_MERIDIAN,
} from "src/constants/enums";

// Contexts
import { useClinicContext } from "src/contexts/clinic/ClinicProvider";

// Types
import {
  AddExamSectionType,
  AddLensSectionType,
  AddVideoSectionType,
  SelectedExamCardType,
} from "src/services/exams/types";
import { ClinicPackageExamType } from "src/services/clinics/types";

// Utils
import { getIncompleteExamsSortedByGroupId } from "src/utils/exams/examReportsHelper";

// Configs
import { priorityLanguageMap } from "src/config/languages";

export const useAddEditExamHelpers = () => {
  const { t } = useTranslation();

  const { clinicInfo } = useClinicContext();

  const { FAR } = positionTypeEnums;
  const { LEFT, RIGHT, BOTH } = eyeTypeEnums;
  const { EYE_TRACKING } = fixationLossMethodEnums;
  const { SPHERE } = fixationPointShapeEnums;

  const currentLang = i18next.language as keyof typeof priorityLanguageMap;

  const defaultSimulationOrder: Array<DefaultExamCardType> = useMemo(() => {
    let examValues = Object.values(examCardEnums);

    // always filter out exams and videos
    examValues = examValues.filter((value) =>
      value.type.includes("Simulation")
    );

    // Remove dry eye simulations if disabled on the clinic
    if (!clinicInfo?.licensing?.canUseDryEyeSimulations) {
      examValues = examValues.filter(
        (value) => value.type !== examCardEnums.DRY_EYE_SIMULATION.type
      );
    }

    // Remove IOL lenses if disabled on the clinic
    if (!clinicInfo?.licensing?.canUseIOLSimulations) {
      examValues = examValues.filter(
        (value) => value.type !== examCardEnums.IOL_SIMULATION.type
      );
    }

    // Remove progressive lenses if disabled on the clinic
    if (!clinicInfo?.licensing?.canUseProgressiveSimulations) {
      examValues = examValues.filter(
        (value) => value.type !== examCardEnums.PROGRESSIVE_SIMULATION.type
      );
    }

    return examValues.sort((a, b) => t(b?.label)?.localeCompare(t(a?.label)));
  }, [
    clinicInfo?.licensing?.canUseDryEyeSimulations,
    clinicInfo?.licensing?.canUseIOLSimulations,
    clinicInfo?.licensing?.canUseProgressiveSimulations,
    t,
  ]);

  // This is the default exam card order when adding exams
  const defaultExamOrder: Array<DefaultExamCardType> = useMemo(() => {
    let examValues = Object.values(examCardEnums);

    // remove all synoptophore exam types if it's disabled on the clinic
    if (!clinicInfo?.licensing?.canUseSynoptophoreExams) {
      examValues = examValues.filter(
        (value) =>
          value.type !== examCardEnums.DEVIATION_AND_CORRESPONDENCE.type &&
          value.type !== examCardEnums.FUSION_AND_VERGENCE.type &&
          value.type !== examCardEnums.STEREOPSIS.type
      );
    }

    // remove deviation and correspondence type if advanced eye tracking is disabled
    if (!clinicInfo?.licensing?.canUseAdvancedEyeTracking) {
      examValues = examValues.filter(
        (value) =>
          value.type !== examCardEnums.DEVIATION_AND_CORRESPONDENCE.type
      );
    }

    // remove RAPD exam type if it's disabled on the clinic
    if (!clinicInfo?.licensing?.canUseRAPDExams) {
      examValues = examValues.filter(
        (value) => value.type !== examCardEnums.RAPD.type
      );
    }

    // remove aniseikonia exam type if it's disabled on the clinic
    if (!clinicInfo?.licensing?.canUseAniseikoniaExams) {
      examValues = examValues.filter(
        (value) => value.type !== examCardEnums.ANISEIKONIA.type
      );
    }

    // remove ocular motility exam type if it's disabled on the clinic
    if (!clinicInfo?.licensing?.canUseOcularMotilityExams) {
      examValues = examValues.filter(
        (value) => value.type !== examCardEnums.OCULAR_MOTILITY.type
      );
    }

    // always filter out lens simulations and videos
    examValues = examValues.filter(
      (value) =>
        !value.type.includes("Simulation") && !value.type.includes("Video")
    );

    // return reverse alphabetical order in english
    return examValues.sort((a, b) => {
      if (currentLang === "fr") {
        return t(a?.label)?.localeCompare(t(b?.label));
      }

      return t(b?.label)?.localeCompare(t(a?.label));
    });
  }, [
    clinicInfo?.licensing?.canUseSynoptophoreExams,
    clinicInfo?.licensing?.canUseAdvancedEyeTracking,
    clinicInfo?.licensing?.canUseRAPDExams,
    clinicInfo?.licensing?.canUseOcularMotilityExams,
    clinicInfo?.licensing?.canUseAniseikoniaExams,
    currentLang,
    t,
  ]);

  const isLensSimulationSections = useCallback(
    (selectedExam: SelectedExamCardType) => {
      const { examFieldType } = selectedExam;
      const isLensSimSection =
        examFieldType === examCardEnums.IOL_SIMULATION.algorithmPath ||
        examFieldType === examCardEnums.PROGRESSIVE_SIMULATION.algorithmPath ||
        examFieldType === examCardEnums.DRY_EYE_SIMULATION.algorithmPath;

      return isLensSimSection;
    },
    []
  );

  const isVfMonocularSections = useCallback(
    (selectedExam: SelectedExamCardType) => {
      const { examFieldType, binocular } = selectedExam;
      const isVfMonocularSections =
        examFieldType.includes(VISUAL_FIELD_PATH) ||
        examFieldType === examCardEnums.VISUAL_FIELD_FULL_120.algorithmPath ||
        examFieldType ===
          examCardEnums.VISUAL_FIELD_SUPERIOR_36.algorithmPath ||
        (!binocular &&
          examFieldType === examCardEnums.VISUAL_FIELD_ESTERMAN.algorithmPath);

      return isVfMonocularSections;
    },
    []
  );

  const isVfBinocularSections = useCallback(
    (selectedExam: SelectedExamCardType) => {
      const { examFieldType, binocular } = selectedExam;
      return (
        binocular &&
        examFieldType === examCardEnums.VISUAL_FIELD_ESTERMAN.algorithmPath
      );
    },
    []
  );

  const getSectionName = useCallback((selectedExam: SelectedExamCardType) => {
    const enumValues = Object.values(examCardEnums);
    const sectionName = enumValues.find(
      (value) => value.algorithmPath === selectedExam.examFieldType
    )?.sectionName;

    return sectionName;
  }, []);

  // Used for IOL simulations
  const getLensSimulationSection = useCallback(
    (eye: string, selectedExam: SelectedExamCardType) => {
      const {
        groupId,
        examType,
        lensIds,
        profileIds,
        simulationEnvironmentType,
        sectionOrder,
      } = selectedExam;

      const getEyeOrder = (): number => {
        switch (eye) {
          case RIGHT:
            return sectionOrder?.right ?? 0;
          case LEFT:
            return sectionOrder?.left ?? 0;
          default:
            return 0;
        }
      };

      let lensSimSection: AddLensSectionType = {
        groupId,
        eye,
        simulationEnvironmentType:
          simulationEnvironmentType ?? DEFAULT_SIM_ENVIRONMENT,
        order: getEyeOrder(),
        algorithm: examType,
        lensIds: lensIds ?? [],
        profileIds: profileIds ?? [],
      };

      return lensSimSection;
    },
    [LEFT, RIGHT]
  );

  // Used for videos
  const getVideoSection = useCallback(
    (selectedExam: SelectedExamCardType) => {
      const { groupId, examType, videoId, videoName, videoVersion, videoUrl } =
        selectedExam;

      let videoSection: AddVideoSectionType = {
        groupId,
        eye: BOTH,
        order: 0,
        algorithm: examType,
        videoId: videoId ?? "",
        videoName: videoName ?? "",
        videoUrl: videoUrl ?? "",
        videoVersion: videoVersion ?? "",
      };

      return videoSection;
    },
    [BOTH]
  );

  // Used for these exams:
  // Visual Field Screening, Visual Field Threshold, Full 120, Esterman
  const getVfEyeSection = useCallback(
    (eye: string, selectedExam: SelectedExamCardType) => {
      const {
        groupId,
        examFieldType,
        examType,
        fovea,
        decibelValue,
        fixationType,
        sectionOrder,
        visualGrid,
        stimulusSizeType,
        stimulusColor,
        speedType,
        optionalToggles,
      } = selectedExam;

      const getEyeOrder = () => {
        switch (eye) {
          case RIGHT:
            return sectionOrder?.right ?? 0;
          case LEFT:
            return sectionOrder?.left ?? 0;
          default:
            return 0;
        }
      };

      let vfEyeSection: AddExamSectionType = {
        groupId,
        algorithm: examType,
        gridType: visualGrid,
        eye,
        fovea: fovea ?? false,
        order: getEyeOrder(),
        stimulusSizeType: stimulusSizeType ?? stimulusTypeEnums.III,
        stimulusColor: stimulusColor ?? stimulusColorEnums.WHITE,
        speedType: speedType ?? speedTypeEnums.NORMAL,
        fixationPointShapeType: fixationType?.fixationPointShapeType ?? SPHERE,
        fixationLossMethodType:
          fixationType?.fixationLossMethod ?? EYE_TRACKING,
        requiresTutorial: optionalToggles?.requiresTutorial ?? false,
        autoPause: optionalToggles?.autoPause ?? false,
        encouragement: optionalToggles?.encouragement ?? false,
        calibration: optionalToggles?.calibration ?? false,
        unlimitedTime: optionalToggles?.unlimitedTime ?? false,
        showProgressRing: optionalToggles?.showProgressRing ?? false,
      };

      // add stimulusColor to certain VF exams
      if (
        examFieldType === examCardEnums.VISUAL_FIELD_SCREENING.algorithmPath ||
        examFieldType === examCardEnums.VISUAL_FIELD_THRESHOLDING.algorithmPath
      ) {
        vfEyeSection = { ...vfEyeSection, stimulusColor };
      }

      // add decibelValue to certain VF exams
      if (
        examFieldType === examCardEnums.VISUAL_FIELD_SCREENING.algorithmPath ||
        examFieldType === examCardEnums.VISUAL_FIELD_FULL_120.algorithmPath ||
        examFieldType === examCardEnums.VISUAL_FIELD_ESTERMAN.algorithmPath ||
        examFieldType === examCardEnums.VISUAL_FIELD_SUPERIOR_36.algorithmPath
      ) {
        vfEyeSection = { ...vfEyeSection, decibelValue };
      }

      // Auto-update the grid type for monocular Esterman exams
      if (
        eye !== BOTH &&
        examFieldType === examCardEnums.VISUAL_FIELD_ESTERMAN.algorithmPath
      ) {
        vfEyeSection = {
          ...vfEyeSection,
          gridType: gridTypeEnums.G_120_M.type,
        };
      }

      return vfEyeSection;
    },
    [RIGHT, LEFT, BOTH, EYE_TRACKING, SPHERE]
  );

  // Used for these exams:
  // Contrast Sensitivity, Color Vision, Visual Acuity, RAPD,
  // Deviation and Correspondence, Fusion and Vergence, Stereopsis, Aniseikonia
  const getEyeSection = useCallback(
    (eye: string, selectedExam: SelectedExamCardType) => {
      const {
        groupId,
        examType,
        examFieldType,
        sectionOrder,
        position,
        startingRowIndex,
        objectiveAlignment,
        subjectiveAngle,
        cyclodeviation,
        fusionalAmplitudes,
        vergenceFunction,
        stereoTargetStyle,
        horizontal,
        vertical,
        overall,
        aniseikoniaTargetStyle,
        aniseikoniaSettingsPerMeridian,
        aniseikoniaObjectiveAlignment,
        optionalToggles,
      } = selectedExam;

      const getEyeOrder = (): number => {
        switch (eye) {
          case RIGHT:
            return sectionOrder?.right ?? 0;
          case LEFT:
            return sectionOrder?.left ?? 0;
          default:
            return 0;
        }
      };

      let eyeSection: AddExamSectionType = {
        groupId,
        algorithm: examType,
        eye,
        order: getEyeOrder(),
        requiresTutorial: optionalToggles?.requiresTutorial ?? false,
        calibration: optionalToggles?.calibration ?? false,
        unlimitedTime: optionalToggles?.unlimitedTime ?? false,
      };

      // add timeLimit to Color Vision exams
      if (examFieldType === examCardEnums.COLOR_VISION.algorithmPath) {
        eyeSection = {
          ...eyeSection,
          timeLimit: optionalToggles.unlimitedTime
            ? -1
            : clinicInfo?.settings?.colorVisionDefaultTimeLimit ?? 120,
        };
      }
      // add position and startingRowIndex to Visual Acuity exams
      if (examFieldType === examCardEnums.VISUAL_ACUITY.algorithmPath) {
        eyeSection = { ...eyeSection, position, startingRowIndex };
      }
      // add unique properties to Deviation and Correspondence exams
      if (
        examFieldType ===
        examCardEnums.DEVIATION_AND_CORRESPONDENCE.algorithmPath
      ) {
        eyeSection = {
          ...eyeSection,
          objectiveAlignment,
          subjectiveAngle,
          cyclodeviation,
        };
      }
      // add unique properties to Fusion and Vergence exams
      if (examFieldType === examCardEnums.FUSION_AND_VERGENCE.algorithmPath) {
        eyeSection = {
          ...eyeSection,
          fusionalAmplitudes,
          vergenceFunction,
        };
      }
      // add unique properties to Stereopsis exams
      if (examFieldType === examCardEnums.STEREOPSIS.algorithmPath) {
        eyeSection = {
          ...eyeSection,
          stereoTargetStyle,
        };
      }
      // add unique properties to Aniseikonia exams. `position` is sent explicitly because the
      // webservice rejects Near rather than quietly measuring at far -- the two distances give
      // different numbers and a clinic would have no way of telling which it got.
      if (examFieldType === examCardEnums.ANISEIKONIA.algorithmPath) {
        eyeSection = {
          ...eyeSection,
          horizontal,
          vertical,
          overall,
          targetStyle: aniseikoniaTargetStyle,
          settingsPerMeridian: aniseikoniaSettingsPerMeridian,
          // Sent back unchanged, never chosen here. Undefined on a card the portal built
          // itself, which the webservice reads as the default of on. What this carries is the
          // other case: a section created through Postman with the steps turned off would
          // quietly get them back on the first time anyone edited the exam, because editing
          // re-adds every section from what the portal sends.
          objectiveAlignment: aniseikoniaObjectiveAlignment,
          position: position ?? FAR,
        };
      }

      return eyeSection;
    },
    [RIGHT, LEFT, FAR, clinicInfo?.settings?.colorVisionDefaultTimeLimit]
  );

  const autopopulateVfCard = useCallback(
    (
      exam: ClinicPackageExamType,
      sectionName: ExamCardSectionNameType,
      examSection: AddExamSectionType
    ) => {
      const currentGroupId = examSection?.groupId ?? 0;

      const defaultVfCard = {
        groupId: currentGroupId,
        examType: examSection?.algorithm ?? "",
        fovea: examSection?.fovea ?? false,
        visualGrid: examSection?.gridType,
        stimulusSizeType: examSection?.stimulusSizeType,
        stimulusColor: examSection?.stimulusColor,
        speedType: examSection?.speedType ?? speedTypeEnums.NORMAL,
        eyeCheckboxes: {
          right: Boolean(
            exam[sectionName]?.find(
              (section) =>
                section.groupId === currentGroupId && section.eye === RIGHT
            )
          ),
          left: Boolean(
            exam[sectionName]?.find(
              (section) =>
                section.groupId === currentGroupId && section.eye === LEFT
            )
          ),
        },
        sectionOrder: {
          right:
            exam[sectionName]?.find(
              (section) =>
                section.groupId === currentGroupId && section.eye === RIGHT
            )?.order ?? 0,
          left:
            exam[sectionName]?.find(
              (section) =>
                section.groupId === currentGroupId && section.eye === LEFT
            )?.order ?? 1,
        },
        fixationType: {
          fixationPointShapeType: examSection?.fixationPointShapeType,
          fixationLossMethod: examSection?.fixationLossMethodType,
        },
        optionalToggles: {
          autoPause: examSection?.autoPause ?? false,
          calibration: examSection?.calibration ?? false,
          encouragement: examSection?.encouragement ?? false,
          requiresTutorial: examSection?.requiresTutorial ?? false,
          showProgressRing: examSection?.showProgressRing ?? false,
        },
      };

      return defaultVfCard;
    },
    [LEFT, RIGHT]
  );

  const autopopulateEstermanCard = useCallback(
    (
      exam: ClinicPackageExamType,
      sectionName: ExamCardSectionNameType,
      examSection: AddExamSectionType
    ) => {
      const currentGroupId = examSection?.groupId ?? 0;

      const defaultEstermanCard = {
        groupId: currentGroupId,
        examType: examSection?.algorithm ?? "",
        visualGrid: examSection?.gridType,
        fovea: examSection?.fovea ?? false,
        binocular: exam[sectionName]
          ?.filter((section) => section.groupId === currentGroupId)
          ?.every((section) => section?.eye === BOTH),
        decibelValue: examSection?.decibelValue
          ? Number(examSection?.decibelValue)
          : DEFAULT_DECIBEL_VALUE_ESTERMAN,
        speedType: examSection?.speedType ?? speedTypeEnums.NORMAL,
        eyeCheckboxes: {
          right: Boolean(
            exam[sectionName]?.find(
              (section) =>
                section.groupId === currentGroupId &&
                (section.eye === RIGHT || section.eye === BOTH)
            )
          ),
          left: Boolean(
            exam[sectionName]?.find(
              (section) =>
                section.groupId === currentGroupId &&
                (section.eye === LEFT || section.eye === BOTH)
            )
          ),
        },
        sectionOrder: {
          right:
            exam[sectionName]?.find(
              (section) =>
                section.groupId === currentGroupId && section.eye === RIGHT
            )?.order ?? 0,
          left:
            exam[sectionName]?.find(
              (section) =>
                section.groupId === currentGroupId && section.eye === LEFT
            )?.order ?? 1,
        },
        fixationType: {
          fixationPointShapeType: examSection?.fixationPointShapeType,
          fixationLossMethod: examSection?.fixationLossMethodType,
        },
        optionalToggles: {
          autoPause: examSection?.autoPause ?? false,
          calibration: examSection?.calibration ?? false,
          encouragement: examSection?.encouragement ?? false,
          requiresTutorial: examSection?.requiresTutorial ?? false,
          showProgressRing: examSection?.showProgressRing ?? false,
        },
      };

      return defaultEstermanCard;
    },
    [BOTH, LEFT, RIGHT]
  );

  const autopopulateNonVfCard = useCallback(
    (
      exam: ClinicPackageExamType,
      sectionName: ExamCardSectionNameType,
      examSection: AddExamSectionType
    ) => {
      const currentGroupId = examSection?.groupId ?? 0;

      const defaultNonVfCard = {
        groupId: currentGroupId,
        examType: examSection?.algorithm ?? "",
        binocular: exam[sectionName]
          ?.filter((section) => section.groupId === currentGroupId)
          ?.every((section) => section?.eye === BOTH),
        eyeCheckboxes: {
          right: Boolean(
            exam[sectionName]?.find(
              (section) =>
                section.groupId === currentGroupId &&
                (section.eye === RIGHT || section.eye === BOTH)
            )
          ),
          left: Boolean(
            exam[sectionName]?.find(
              (section) =>
                section.groupId === currentGroupId &&
                (section.eye === LEFT || section.eye === BOTH)
            )
          ),
        },
        sectionOrder: {
          right:
            exam[sectionName]?.find(
              (section) =>
                section.groupId === currentGroupId && section.eye === RIGHT
            )?.order ?? 0,
          left:
            exam[sectionName]?.find(
              (section) =>
                section.groupId === currentGroupId && section.eye === LEFT
            )?.order ?? 1,
        },
      };

      return defaultNonVfCard;
    },
    [BOTH, LEFT, RIGHT]
  );

  const autopopulateLensSimCard = useCallback(
    (
      exam: ClinicPackageExamType,
      sectionName: ExamCardSectionNameType,
      examSection: AddExamSectionType
    ) => {
      const currentGroupId = examSection?.groupId ?? 0;
      const lensIds =
        examSection?.lensIds ??
        examSection?.lenses?.map((lens) => lens.id) ??
        [];
      const profileIds =
        examSection?.profileIds ??
        examSection?.profiles?.map((profile) => profile.id) ??
        [];

      const defaultLensSimCard = {
        groupId: currentGroupId,
        examType: examSection?.algorithm ?? "",
        lensIds,
        profileIds,
        simulationEnvironmentType: examSection?.simulationEnvironmentType ?? "",
        binocular: exam[sectionName]
          ?.filter((section) => section.groupId === currentGroupId)
          ?.every((section) => section?.eye === BOTH),
        eyeCheckboxes: {
          right: Boolean(
            exam[sectionName]?.find(
              (section) =>
                section.groupId === currentGroupId &&
                (section.eye === RIGHT || section.eye === BOTH)
            )
          ),
          left: Boolean(
            exam[sectionName]?.find(
              (section) =>
                section.groupId === currentGroupId &&
                (section.eye === LEFT || section.eye === BOTH)
            )
          ),
        },
        sectionOrder: {
          right:
            exam[sectionName]?.find(
              (section) =>
                section.groupId === currentGroupId && section.eye === RIGHT
            )?.order ?? 0,
          left:
            exam[sectionName]?.find(
              (section) =>
                section.groupId === currentGroupId && section.eye === LEFT
            )?.order ?? 1,
        },
        optionalToggles: {},
      };

      return defaultLensSimCard;
    },
    [BOTH, LEFT, RIGHT]
  );

  const autopopulateVideoCard = useCallback(
    (examSection: AddExamSectionType) => {
      const currentGroupId = examSection?.groupId ?? 0;
      const videoId = examSection?.videoId ?? "";
      const videoName = examSection?.videoName ?? "";

      const defaultVideoCard = {
        groupId: currentGroupId,
        videoId,
        videoName,
        examType: examSection?.algorithm ?? "",
        binocular: examSection?.eye === BOTH,
        eyeCheckboxes: {
          right: true,
          left: true,
        },
        optionalToggles: {},
      };

      return defaultVideoCard;
    },
    [BOTH]
  );

  const generateExamsToEdit = useCallback(
    (exam: ClinicPackageExamType) => {
      const examCardsToEdit: Array<SelectedExamCardType> = [];

      const enumValues = Object.values(examCardEnums);
      const sortedByGroupId = getIncompleteExamsSortedByGroupId(exam);
      const largestGroupId = sortedByGroupId?.[0]?.groupId ?? 0;
      const arrayLength = largestGroupId + 1;

      // Iterate over the groupIds
      Array.from({ length: arrayLength }).forEach((_, index) => {
        const examSection = sortedByGroupId.find(
          (section) => section.groupId === index || section.groupId == null
        );

        if (!examSection) return;

        const enumValue = enumValues.find(
          (value) => value.sectionName === examSection.sectionName
        );
        const examFieldType = enumValue?.algorithmPath;
        const sectionName = examSection.sectionName;

        if (!examFieldType || !sectionName) return;

        switch (sectionName) {
          case examCardEnums.VIDEO.sectionName:
            const editVideoCard = {
              ...autopopulateVideoCard(examSection),
              examFieldType,
            };
            examCardsToEdit.push(editVideoCard);
            break;

          case examCardEnums.PROGRESSIVE_SIMULATION.sectionName:
          case examCardEnums.IOL_SIMULATION.sectionName:
          case examCardEnums.DRY_EYE_SIMULATION.sectionName:
            const editLensSimCard = {
              ...autopopulateLensSimCard(exam, sectionName, examSection),
              examFieldType,
            };
            examCardsToEdit.push(editLensSimCard);
            break;

          case examCardEnums.VISUAL_FIELD_THRESHOLDING.sectionName:
            const editVfThresholdCard = {
              ...autopopulateVfCard(exam, sectionName, examSection),
              stimulusColor:
                examSection?.stimulusColor ?? stimulusColorEnums.WHITE,
              examFieldType,
            };
            examCardsToEdit.push(editVfThresholdCard);
            break;

          case examCardEnums.VISUAL_FIELD_SCREENING.sectionName:
            const editVfScreenCard = {
              ...autopopulateVfCard(exam, sectionName, examSection),
              examFieldType,
              stimulusColor:
                examSection?.stimulusColor ?? stimulusColorEnums.WHITE,
              decibelValue: examSection?.decibelValue
                ? Number(examSection?.decibelValue)
                : DEFAULT_DECIBEL_VALUE_SCREENING,
            };
            examCardsToEdit.push(editVfScreenCard);
            break;

          case examCardEnums.VISUAL_FIELD_FULL_120.sectionName:
            const editFull120Card = {
              ...autopopulateVfCard(exam, sectionName, examSection),
              examFieldType,
              decibelValue: examSection?.decibelValue
                ? Number(examSection?.decibelValue)
                : DEFAULT_DECIBEL_VALUE_FULL_120,
            };
            examCardsToEdit.push(editFull120Card);
            break;

          case examCardEnums.VISUAL_FIELD_SUPERIOR_36.sectionName:
            const editSuperior36Card = {
              ...autopopulateVfCard(exam, sectionName, examSection),
              examFieldType,
              decibelValue: examSection?.decibelValue
                ? Number(examSection?.decibelValue)
                : DEFAULT_DECIBEL_VALUE_SUPERIOR_36,
            };
            examCardsToEdit.push(editSuperior36Card);
            break;

          case examCardEnums.VISUAL_FIELD_ESTERMAN.sectionName:
            const editEstermanCard = {
              ...autopopulateEstermanCard(exam, sectionName, examSection),
              examFieldType,
            };
            examCardsToEdit.push(editEstermanCard);
            break;

          case examCardEnums.CONTRAST_SENSITIVITY.sectionName:
            const editContrastSensitivityCard = {
              ...autopopulateNonVfCard(exam, sectionName, examSection),
              examFieldType,
              optionalToggles: {
                requiresTutorial: examSection?.requiresTutorial ?? false,
              },
            };
            examCardsToEdit.push(editContrastSensitivityCard);
            break;

          case examCardEnums.COLOR_VISION.sectionName:
            const editColorVisionCard = {
              ...autopopulateNonVfCard(exam, sectionName, examSection),
              examFieldType,
              optionalToggles: {
                requiresTutorial: examSection?.requiresTutorial ?? false,
                unlimitedTime: Boolean(examSection?.timeLimit === -1),
              },
            };
            examCardsToEdit.push(editColorVisionCard);
            break;

          case examCardEnums.VISUAL_ACUITY.sectionName:
            const editVisualAcuityCard = {
              ...autopopulateNonVfCard(exam, sectionName, examSection),
              examFieldType,
              startingRowIndex: !examSection?.startingRowIndex
                ? DEFAULT_STARTING_ROW_INDEX
                : examSection?.startingRowIndex,
              position: examSection?.position ?? FAR,
              optionalToggles: {
                requiresTutorial: examSection?.requiresTutorial ?? false,
              },
            };
            examCardsToEdit.push(editVisualAcuityCard);
            break;

          case examCardEnums.DEVIATION_AND_CORRESPONDENCE.sectionName:
            const editDeviationCard = {
              ...autopopulateNonVfCard(exam, sectionName, examSection),
              examFieldType,
              objectiveAlignment: examSection?.objectiveAlignment ?? true,
              subjectiveAngle: examSection?.subjectiveAngle ?? false,
              cyclodeviation: examSection?.cyclodeviation ?? false,
              optionalToggles: {},
            };
            examCardsToEdit.push(editDeviationCard);
            break;

          case examCardEnums.FUSION_AND_VERGENCE.sectionName:
            const editFusionCard = {
              ...autopopulateNonVfCard(exam, sectionName, examSection),
              examFieldType,
              fusionalAmplitudes: examSection?.fusionalAmplitudes ?? false,
              vergenceFunction: examSection?.vergenceFunction ?? false,
              optionalToggles: {},
            };
            examCardsToEdit.push(editFusionCard);
            break;

          case examCardEnums.STEREOPSIS.sectionName:
            const editStereopsisCard = {
              ...autopopulateNonVfCard(exam, sectionName, examSection),
              examFieldType,
              stereoTargetStyle:
                examSection?.stereoTargetStyle ?? stereoTargetStyleOptions[0],
              optionalToggles: {},
            };
            examCardsToEdit.push(editStereopsisCard);
            break;

          case examCardEnums.ANISEIKONIA.sectionName:
            const editAniseikoniaCard = {
              ...autopopulateNonVfCard(exam, sectionName, examSection),
              examFieldType,
              position: examSection?.position ?? FAR,
              horizontal: examSection?.horizontal ?? false,
              vertical: examSection?.vertical ?? false,
              overall: examSection?.overall ?? false,
              aniseikoniaTargetStyle:
                examSection?.targetStyle ?? aniseikoniaTargetStyleOptions[0],
              aniseikoniaSettingsPerMeridian:
                examSection?.settingsPerMeridian ??
                DEFAULT_ANISEIKONIA_SETTINGS_PER_MERIDIAN,
              aniseikoniaObjectiveAlignment: examSection?.objectiveAlignment,
              optionalToggles: {},
            };
            examCardsToEdit.push(editAniseikoniaCard);
            break;

          case examCardEnums.RAPD.sectionName:
            const editRAPDCard = {
              ...autopopulateNonVfCard(exam, sectionName, examSection),
              examFieldType,
              optionalToggles: {
                requiresTutorial: examSection?.requiresTutorial ?? false,
                calibration: examSection?.calibration ?? false,
              },
            };
            examCardsToEdit.push(editRAPDCard);
            break;

          case examCardEnums.OCULAR_MOTILITY.sectionName:
            const editOcularMotilityCard = {
              ...autopopulateNonVfCard(exam, sectionName, examSection),
              examFieldType,
              optionalToggles: {
                requiresTutorial: examSection?.requiresTutorial ?? true, // always true
                calibration: examSection?.calibration ?? false,
              },
            };
            examCardsToEdit.push(editOcularMotilityCard);
            break;

          default:
          // do nothing
        }
      });

      // format the exam card labels
      let indexNums: { [key: string]: number } = {};
      const examCardsWithLabels = examCardsToEdit.map((examCard) => {
        const hasMultiSameExams =
          examCardsToEdit.filter(
            (exam) => exam.examFieldType === examCard.examFieldType
          )?.length > 1;

        if (hasMultiSameExams) {
          indexNums = {
            ...indexNums,
            [examCard.examFieldType]:
              (indexNums?.[examCard.examFieldType] ?? 0) + 1,
          };
          return {
            ...examCard,
            examNumLabel: indexNums?.[examCard.examFieldType],
          };
        } else {
          return { ...examCard, examNumLabel: 1 };
        }
      });

      return examCardsWithLabels;
    },
    [
      FAR,
      autopopulateVfCard,
      autopopulateEstermanCard,
      autopopulateNonVfCard,
      autopopulateLensSimCard,
      autopopulateVideoCard,
    ]
  );

  return {
    defaultExamOrder,
    defaultSimulationOrder,
    isLensSimulationSections,
    isVfMonocularSections,
    isVfBinocularSections,
    getSectionName,
    getVfEyeSection,
    getEyeSection,
    getVideoSection,
    getLensSimulationSection,
    generateExamsToEdit,
  };
};
